/**
 * The Equipment step's ONE selection (`equipment-page-table`, R11–R12): a set of register row ids
 * for the twin, shared by the Rows and Groups views, so ticking a group's rows and switching to Rows
 * keeps them ticked. `bulk-actions` acts on it; `confirm` selects through it.
 *
 * Pruned so every action acts on what the person sees:
 * - cleared when the chip or the search changes (`filterKey`), and when the twin changes;
 * - cut to the ids still in the register after every re-read;
 * - cut to the rows the chip and search show, so it never holds a hidden row;
 * - cleared by the host after a removal lands (`clear`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

export interface EquipmentSelection {
  /** In the order they were ticked. */
  selected: number[];
  set: (keys: readonly (string | number)[]) => void;
  clear: () => void;
}

export const useEquipmentSelection = ({
  twinId,
  filterKey,
  registerIds,
  visibleIds,
}: {
  twinId: number | null | undefined;
  filterKey: string;
  /** Every id in the register as last read. */
  registerIds: ReadonlySet<number>;
  /** The ids the chip and search show; undefined shows every row. */
  visibleIds: ReadonlySet<number> | undefined;
}): EquipmentSelection => {
  const [ticked, setTicked] = useState<number[]>([]);

  // A new filter or a new twin starts empty: the old ticks named rows chosen under another view.
  useEffect(() => {
    setTicked([]);
  }, [filterKey, twinId]);

  // Never a row the register no longer holds, or one the chip and search hide.
  const selected = useMemo(
    () =>
      ticked.filter(id => registerIds.has(id) && (visibleIds === undefined || visibleIds.has(id))),
    [ticked, registerIds, visibleIds],
  );

  // And forgotten, not just hidden: a row that left the register or the view does not come back
  // ticked when it returns (Copilot, PR #2157). The filter above keeps every action safe meanwhile.
  useEffect(() => {
    if (selected.length !== ticked.length) setTicked(selected);
  }, [selected, ticked]);

  const set = useCallback((keys: readonly (string | number)[]) => {
    setTicked(keys.filter((key): key is number => typeof key === 'number'));
  }, []);
  const clear = useCallback(() => setTicked([]), []);

  return { selected, set, clear };
};
